// VideoTile.jsx

import React, { useEffect, useRef, useState } from 'react';

// Services
import { resolvePlayableVideoUrl, resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './VideoTile.css';

/**
 * VideoTile
 *
 * Displays a video node in the NodeGrid.
 *
 * @param {object} node - The video node.
 * @returns {ReactNode} VideoTile component.
 */
const VideoTile = React.forwardRef(({ node }, ref) => {

    const videoRef = useRef(null);
    const [videoSrc, setVideoSrc] = useState(null);
    const [coverSrc, setCoverSrc] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const url = await resolvePlayableVideoUrl(node);
            if (cancelled) return;
            setVideoSrc(url || null);
        })();
        return () => {
            cancelled = true;
        };
    }, [node]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const url = await resolveImageUrl({ nodeKey: node?.key || null, url: node?.coverUrl || null });
            if (cancelled) return;
            setCoverSrc(url || null);
        })();

        return () => {
            cancelled = true;
        };
    }, [node]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    video.play().catch(() => {});
                } else {
                    video.pause();
                }
            },
            { threshold: 0.5 }
        );

        observer.observe(video);

        return () => observer.disconnect();
    }, []);

    return (
        <div className="video-tile-container" title={node.title}>

            {/* VIDEO */}
            {node.coverUrl ? (
                <img
                  className="video-tile-image"
                  src={coverSrc || node.coverUrl}
                  alt={node.title}
                />
            ) : (
                <video
                  className="video-tile-video"
                  ref={(el) => {
                        videoRef.current = el;
                        if (ref) {
                            if (typeof ref === 'function') {
                                ref(el);
                            } else {
                                ref.current = el;
                            }
                        }
                    }}
                  src={videoSrc || undefined}
                  muted
                  playsInline
                  autoPlay
                  loop
                  controls={false}
                />
            )}
        </div>
    );
});

// Dev Tools
VideoTile.displayName = 'VideoTile';

export default VideoTile;
